/**
 * Three.js presentation layer.
 *
 * Deliberately fed the *collision* geometry rather than an art asset: the world
 * you can see and the world you can walk into are the same list of boxes. Props
 * now carry real meshes (`prop-meshes.ts`), and because those are authored
 * inside the unit cube the per-instance transform already scales by the hull,
 * the shape changed without a second set of dimensions appearing anywhere. Where
 * a mesh insets from its hull — a barrel is a cylinder, so its corners are empty
 * — F4 draws the hull over it as a wireframe, because "the model and the hull
 * have drifted apart" is otherwise invisible until someone gets shot through a
 * wall.
 *
 * Level geometry (floor, cover, walls) is static and drawn once from
 * `world.solids`, styled by kind. See `buildScene` for why the map is passed in
 * as *appearance* data only, and `batches.ts` for the grouping that decides
 * which solid depicts which wall. Surfaces come from `materials.ts`, which
 * projects UVs from world position so that one shared unit cube can be textured
 * at all — see that file's header, it is the load-bearing trick here.
 *
 * Props are not drawn from map data at all — see the M3 note on
 * `buildPropInstances`. Every placed prop, worn or not, now arrives as a live
 * entity every tick (that is what closes the possession gap: a hunter's client
 * can no longer tell a still hider from scenery by which prop instances even
 * appear on the wire), so the renderer has to be able to move any of them,
 * every frame, without falling back to one draw call per prop.
 */

import * as THREE from "three";
import {
  DEFAULT_HULL,
  PLAYER_EYE_HEIGHT,
  eyeHeightFor,
  type GameMap,
  type PropCatalogue,
  type PropDef,
  type StaticWorld,
} from "@prop-hunt/shared";
import { propInstanceGroups, renderBatches, type RenderBatch } from "./batches.js";
import { ceilingHeight, fixturePositions, mapBounds } from "./decor.js";
import {
  boxProjected,
  colorForId,
  colorMap,
  propMaterial,
  themeFor,
  type Theme,
} from "./materials.js";
import { propGeometry } from "./prop-meshes.js";

/** Metres between ceiling fittings, and the half-extent of one. */
const FIXTURE_SPACING = 8;
const FIXTURE_HALF = 0.6;

/**
 * How many fittings get an actual `PointLight`.
 *
 * The forward renderer unrolls its light loop and evaluates every light for
 * every fragment — there is no culling — so this is a per-pixel cost paid across
 * the whole screen, not just under the fittings. The rest of the fittings are
 * emissive panels: they read as sources without being ones, which at a glance
 * is indistinguishable and is free.
 */
const MAX_FIXTURE_LIGHTS = 12;

export interface AvatarPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  /**
   * Catalogue id this entity is wearing, or 0 for an undisguised player.
   *
   * The box drawn for a disguised player is built from the *same* `PropDef
   * .hull` the simulation collides with, never from a second set of numbers.
   * A disguise you can see but not hit — or hit but not see — is the one
   * rendering bug this game cannot tolerate, since the entire mechanic is
   * players deciding whether a crate is a crate.
   */
  propId: number;
  /** Rendered differently so interpolation starvation is visible, not inferred. */
  starved: boolean;
}

export class GameRenderer {
  readonly camera: THREE.PerspectiveCamera;

  private readonly scene = new THREE.Scene();
  private readonly renderer: THREE.WebGLRenderer;
  private readonly avatars = new Map<number, THREE.Group>();
  /** The *own* material of each avatar, used only while it is undisguised. */
  private readonly avatarMaterials = new Map<number, THREE.MeshStandardMaterial>();
  /** Catalogue id each avatar is currently *shaped* as, to skip no-op reshapes. */
  private readonly avatarProps = new Map<number, number>();
  private readonly bodyGeometry: THREE.BoxGeometry;
  private readonly noseGeometry: THREE.ConeGeometry;

  /**
   * The one geometry and the one material for each prop kind, keyed by
   * `PropDef.mesh`.
   *
   * Shared, and that is the point. Both the kind's `InstancedMesh` and any
   * player disguised as it draw from these exact objects, so a disguise cannot
   * be a slightly different crate to the crates around it — not because two
   * lookups happen to agree today, but because there is only one thing to look
   * up. In a game whose entire mechanic is deciding whether a crate is a crate,
   * that is a correctness property rather than a tidiness one.
   */
  private readonly propAssets = new Map<
    string,
    { geometry: THREE.BufferGeometry; material: THREE.MeshStandardMaterial }
  >();

  /** Hull wireframes, one batch per kind, hidden until F4 asks for them. */
  private readonly hullMeshes: THREE.InstancedMesh[] = [];

  /**
   * Where each placed prop's instance lives, keyed by `propIndex` (an index
   * into `GameMap.props` — the same number a `Msg.Snapshot` entity's id
   * decodes to via `propIndexFromEntityId`).
   *
   * Fixed at construction by `buildPropInstances`: a prop cannot change kind
   * or slot, only position, so this never needs to grow, shrink or be
   * rebuilt for the life of a level.
   */
  private readonly propInstances = new Map<
    number,
    {
      mesh: THREE.InstancedMesh;
      /** The wireframe hull drawn over `mesh`, moved with it. */
      hulls: THREE.InstancedMesh;
      slot: number;
      scale: THREE.Vector3;
      halfHeight: number;
    }
  >();
  /** Reused across every `setPropPose` call so posing 101 props allocates nothing. */
  private readonly propMatrixScratch = new THREE.Matrix4();

  /** The level's palette. Kept so avatars can be built against it too. */
  private readonly theme: Theme;
  /** `getMaxAnisotropy()` once, since every texture wants it. */
  private readonly anisotropy: number;

  /** All materials created by this renderer, for cleanup. */
  private readonly createdMaterials: THREE.Material[] = [];
  /** All textures created by this renderer, for cleanup. */
  private readonly createdTextures: THREE.Texture[] = [];
  /** All geometries created by this renderer, for cleanup. */
  private readonly createdGeometries: THREE.BufferGeometry[] = [];

  constructor(
    canvas: HTMLCanvasElement,
    world: StaticWorld,
    map: GameMap,
    /** Kept, not just consumed: disguised avatars are sized from it at runtime. */
    private readonly catalogue: PropCatalogue,
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.theme = themeFor(map.name);

    // Tone mapping is set once, here, and never changed: it is part of every
    // material's program key, so touching it at runtime recompiles the scene.
    // Neutral rather than ACES — ACES is tuned for filmic HDR footage, and on
    // flat art-directed colours it desaturates everything towards grey.
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.shadowMap.enabled = true;
    // PCFSoftShadowMap is deprecated in this version and silently downgrades to
    // PCF with a console warning. PCF here *is* the soft one — a rotated
    // Vogel-disk kernel — so softness comes from `shadow.radius` below.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // The shadow pass runs before the renderer resets its own counters, so the
    // F3 overlay would report the main pass only. Reset manually in `render`
    // instead, and the numbers on screen are the numbers being drawn.
    this.renderer.info.autoReset = false;

    this.scene.background = new THREE.Color(this.theme.sky);
    this.scene.fog = new THREE.Fog(this.theme.sky, this.theme.fogNear, this.theme.fogFar);

    this.camera = new THREE.PerspectiveCamera(75, 1, 0.1, 500);
    // Three.js's camera looks down -Z at zero rotation, which is why yaw 0
    // means -Z in the movement code too. Keeping the two conventions identical
    // avoids a sign error that would only show up as "forward is sideways".
    this.camera.rotation.order = "YXZ";

    this.buildLights(world);
    this.buildScene(world, map, catalogue);

    this.bodyGeometry = new THREE.BoxGeometry(
      DEFAULT_HULL.halfWidth * 2,
      DEFAULT_HULL.height,
      DEFAULT_HULL.halfDepth * 2,
    );
    this.noseGeometry = new THREE.ConeGeometry(0.12, 0.35, 8);

    this.resize();
  }

  /**
   * Hemisphere fill plus one overhead key light that casts every shadow in the
   * level.
   *
   * On an indoor level the key light is a fiction and is meant to be: once the
   * map is enclosed by walls and a ceiling there is no window for a sun to come
   * through. What it buys is contact shadows under props and desks, which is
   * most of what makes geometry read as sitting on the floor rather than
   * hovering over it. The ceiling is excluded from casting (see
   * `buildCeiling`) or it would shadow the entire interior.
   *
   * On an outdoor one (`theme.ceiling === null`) the same light is simply the
   * sun, and is the only light there is — which is why those themes carry a
   * much stronger `sunIntensity`. Nothing here needs to branch on it.
   *
   * Everything here is built exactly once. The light *count* is part of every
   * material's program key and is recomputed each frame from what is in the
   * scene, so adding, removing or hiding a light later would recompile every
   * shader in the level — a visible hitch, for a light nobody asked to move.
   */
  private buildLights(world: StaticWorld): void {
    const bounds = mapBounds(world);
    const ceiling = ceilingHeight(world);

    this.scene.add(
      new THREE.HemisphereLight(this.theme.hemiSky, this.theme.hemiGround, this.theme.hemiIntensity),
    );

    const key = new THREE.DirectionalLight(this.theme.sun, this.theme.sunIntensity);
    key.position.set(bounds.maxX * 0.4, ceiling * 14, bounds.minZ * 0.55);
    key.castShadow = true;
    // The ortho box is in light space, so it has to cover the map's diagonal
    // rather than its width, or corners fall outside and stop casting.
    const half =
      Math.hypot(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / 2 + 2;
    key.shadow.camera.left = -half;
    key.shadow.camera.right = half;
    key.shadow.camera.top = half;
    key.shadow.camera.bottom = -half;
    key.shadow.camera.near = 1;
    key.shadow.camera.far = ceiling * 20;
    key.shadow.camera.updateProjectionMatrix();
    key.shadow.mapSize.set(2048, 2048);
    // Both default to 0. Instanced unit cubes at large non-uniform scale are
    // acne-prone, and normalBias is the one that does not detach contact
    // shadows from the thing casting them.
    key.shadow.normalBias = 0.04;
    key.shadow.bias = -0.0005;
    key.shadow.radius = 3;
    this.scene.add(key);
    this.scene.add(key.target);
  }

  /**
   * One draw call per *kind* of level-geometry solid, rather than one per
   * solid. Props are excluded here — see `buildPropInstances` — because they
   * move at runtime and a batch built once from `world.solids` can't move.
   *
   * The geometry still comes from `world.solids` and nowhere else. The map is
   * consulted only to decide how a solid should *look*, which is what makes
   * drift structurally impossible: there is no second derivation of a wall's
   * box that could disagree with the one movement queries. `renderBatches`
   * owns that bucketing and is tested directly.
   *
   * Why instancing rather than a mesh apiece. depot is 87 wall solids, which
   * the naive version turned into 87 meshes with 87 separate `BoxGeometry`
   * uploads. That is entirely CPU-bound per-object overhead for a level small
   * enough to fit in a single vertex buffer, and it scales with how *detailed*
   * a map is, which is exactly the direction maps grow. Every box is a unit
   * cube under an affine transform, so one shared geometry plus a per-instance
   * matrix draws the whole batch at once — a handful of calls instead of
   * dozens.
   *
   * The tradeoff is that frustum culling becomes per batch instead of per box,
   * so off-camera walls are no longer skipped individually. At this triangle
   * count that is not a cost worth paying attention to — the level is smaller
   * than a single character model — and fog far (130m) already exceeds the
   * map's 80x56m diagonal, so most of it was on screen anyway.
   */
  private buildScene(world: StaticWorld, map: GameMap, catalogue: PropCatalogue): void {
    // One unit cube, shared by every batch. Per-instance matrices scale it to
    // each solid's extents, so the vertex data is uploaded exactly once.
    const unitBox = new THREE.BoxGeometry(1, 1, 1);
    this.createdGeometries.push(unitBox);
    const matrix = new THREE.Matrix4();
    const storey = ceilingHeight(world);

    for (const batch of renderBatches(world, map, catalogue)) {
      if (batch.kind === "prop") continue;

      const mesh = new THREE.InstancedMesh(
        unitBox,
        this.levelMaterial(batch, storey),
        batch.boxes.length,
      );
      // Level geometry both casts and receives: a wall that does not cast
      // leaves the room behind it lit through it, and a floor that does not
      // receive is a floor props appear to float above.
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      batch.boxes.forEach((box, i) => {
        // makeScale then setPosition: setPosition writes the translation
        // column and leaves the scale intact, which is the whole transform for
        // an axis-aligned box.
        matrix.makeScale(box.w, box.h, box.d);
        matrix.setPosition(box.cx, box.cy, box.cz);
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;

      this.scene.add(mesh);
    }

    // An outdoor level has no lid and no light fittings to hang off one. The
    // sun set up in `buildLights` is all the light it gets, which is the right
    // amount for somewhere with a sky over it.
    if (this.theme.ceiling !== null) this.buildCeiling(world, storey);
    this.buildPropInstances(map, catalogue);
  }

  /**
   * The surface for one level batch.
   *
   * All three take their UVs from world position rather than from the shared
   * cube's own 0..1 — see `materials.ts`, which is where the reasoning lives.
   * Lambert rather than Standard because floors and walls are most of the
   * screen and the ceiling's fittings are evaluated per fragment with no
   * culling; carpet and emulsion have no specular worth the GGX.
   */
  private levelMaterial(batch: RenderBatch, storey: number): THREE.Material {
    const anisotropy = this.anisotropy;

    if (batch.kind === "floor") {
      const floor = colorMap(this.theme.floor(), anisotropy);
      this.createdTextures.push(floor);
      const material = boxProjected(new THREE.MeshLambertMaterial(), {
        side: floor,
        top: floor,
        sideRepeat: this.theme.floorRepeat,
        sideHeight: this.theme.floorRepeat,
        topRepeat: this.theme.floorRepeat,
        cacheKey: "floor",
      });
      this.createdMaterials.push(material);
      return material;
    }

    if (batch.kind === "cover") {
      // A desk run: laminate where you would set a mug down, fabric on the
      // sides. One material, chosen per fragment by the face normal, because a
      // material array on a BoxGeometry costs a draw call per face.
      const side = colorMap(this.theme.coverSide(), anisotropy);
      const top = colorMap(this.theme.coverTop(), anisotropy);
      this.createdTextures.push(side, top);
      const material = boxProjected(new THREE.MeshLambertMaterial(), {
        side,
        top,
        sideRepeat: this.theme.coverRepeat,
        sideHeight: this.theme.coverRepeat,
        topRepeat: this.theme.coverRepeat,
        cacheKey: "cover",
      });
      this.createdMaterials.push(material);
      return material;
    }

    // Walls. `sideHeight` is the storey height, which turns the wall texture
    // from a tiling swatch into an elevation: its skirting board lands on the
    // floor and its picture rail under the ceiling, on every wall in the level,
    // with no trim geometry to collide with. `wrapT` is clamped so the image
    // does not repeat up the wall.
    //
    // A wall's top face is the underside of the ceiling it meets, so indoors it
    // wears the ceiling's surface. Outdoors there is no ceiling to meet and the
    // top is a coping stone at the head of the wall, which is what the cover
    // surface already is.
    const side = colorMap(this.theme.wall(storey), anisotropy, THREE.ClampToEdgeWrapping);
    const top = colorMap((this.theme.ceiling ?? this.theme.coverTop)(), anisotropy);
    this.createdTextures.push(side, top);
    const material = boxProjected(new THREE.MeshLambertMaterial(), {
      side,
      top,
      sideRepeat: this.theme.wallRepeat,
      sideHeight: storey,
      topRepeat: this.theme.ceilingRepeat,
      cacheKey: "wall",
    });
    this.createdMaterials.push(material);
    return material;
  }

  /**
   * A lid on the level, and the fittings hanging off it.
   *
   * Single-sided and facing *down*. A player who jumps off a crate can put
   * their eye above 3m — jump apex is 1.11m and eye height 1.65 — and a
   * double-sided ceiling would black their screen out at the top of the jump.
   * Backface culling turns that into a brief look down into the level instead,
   * which is odd for a frame and not a bug anyone can be hurt by.
   *
   * `castShadow` is off, and has to be: the key light is overhead, so a
   * shadow-casting ceiling would put the whole interior in shade.
   */
  private buildCeiling(world: StaticWorld, storey: number): void {
    const ceiling = this.theme.ceiling;
    if (ceiling === null) return;

    const bounds = mapBounds(world);
    const width = bounds.maxX - bounds.minX;
    const depth = bounds.maxZ - bounds.minZ;

    const ceilingMap = colorMap(ceiling(), this.anisotropy);
    this.createdTextures.push(ceilingMap);
    ceilingMap.repeat.set(width / this.theme.ceilingRepeat, depth / this.theme.ceilingRepeat);

    const lidMaterial = new THREE.MeshLambertMaterial({ map: ceilingMap });
    this.createdMaterials.push(lidMaterial);
    const lid = new THREE.Mesh(
      new THREE.PlaneGeometry(width, depth),
      lidMaterial,
    );
    this.createdGeometries.push(lid.geometry as THREE.BufferGeometry);
    // PlaneGeometry faces +Z; a quarter turn about X points it at -Y.
    lid.rotation.x = Math.PI / 2;
    lid.position.set((bounds.minX + bounds.maxX) / 2, storey, (bounds.minZ + bounds.maxZ) / 2);
    lid.castShadow = false;
    lid.receiveShadow = false;
    this.scene.add(lid);

    const fixtures = fixturePositions(world, FIXTURE_SPACING, FIXTURE_HALF);
    if (fixtures.length === 0) return;

    // The panels themselves: emissive, so they read as the source of the light
    // without being lights. One instanced batch for all of them.
    const panel = new THREE.MeshBasicMaterial({ color: this.theme.fixture });
    this.createdMaterials.push(panel);
    const panelGeometry = new THREE.BoxGeometry(FIXTURE_HALF * 2, 0.06, FIXTURE_HALF * 2);
    this.createdGeometries.push(panelGeometry);
    const panels = new THREE.InstancedMesh(panelGeometry, panel, fixtures.length);
    const matrix = new THREE.Matrix4();
    fixtures.forEach((f, i) => {
      matrix.makeTranslation(f.x, storey - 0.04, f.z);
      panels.setMatrixAt(i, matrix);
    });
    panels.instanceMatrix.needsUpdate = true;
    this.scene.add(panels);

    // Only some of them are real lights. Spread the ones that are evenly
    // through the list rather than taking the first N, or one end of the map
    // gets all of them.
    const step = Math.max(1, Math.ceil(fixtures.length / MAX_FIXTURE_LIGHTS));
    for (let i = 0; i < fixtures.length; i += step) {
      const f = fixtures[i]!;
      const lamp = new THREE.PointLight(
        this.theme.fixture,
        this.theme.fixtureIntensity,
        FIXTURE_SPACING * 2.4,
      );
      lamp.position.set(f.x, storey - 0.25, f.z);
      // Never shadowed: a shadow-casting point light is six render passes.
      lamp.castShadow = false;
      this.scene.add(lamp);
    }
  }

  /**
   * One `InstancedMesh` per catalogue *kind*, holding every placed prop of
   * that kind — moved every tick by `setPropPose` rather than rebuilt.
   *
   * M3 note: a prop's position used to be baked in here once, from map data,
   * because only a *disguised* prop ever moved and disguises were rare enough
   * to render individually (see `AvatarPose`). Closing the possession gap
   * means every prop, worn or not, arrives as a live entity every tick — so
   * "individually" would mean up to 101 extra draw calls on depot, undoing
   * the instancing work above. Instead, layout is fixed once by
   * `propInstanceGroups` (a prop cannot change kind or slot, only position)
   * and `setPropPose` rewrites one instance's matrix per update, which is the
   * same per-instance-matrix trick `buildScene` uses for walls, applied to
   * something that moves.
   *
   * Shape and colour are read once from `PropCatalogue`, the same hull the
   * simulation collides with — no second set of numbers here to drift from it.
   * Only the transform is ever touched again.
   *
   * Seeded at each prop's *authored* position so nothing flashes to the
   * origin before the first snapshot arrives; every prop is present in every
   * snapshot from the first one, so this placeholder is overwritten within a
   * tick of connecting.
   */
  private buildPropInstances(map: GameMap, catalogue: PropCatalogue): void {
    const hullBox = new THREE.BoxGeometry(1, 1, 1);
    this.createdGeometries.push(hullBox);
    const hullMaterial = new THREE.MeshBasicMaterial({ color: 0x33ff88, wireframe: true });
    this.createdMaterials.push(hullMaterial);
    const matrix = new THREE.Matrix4();

    for (const group of propInstanceGroups(map, catalogue)) {
      const { halfWidth, halfDepth, height } = group.def.hull;
      const scale = new THREE.Vector3(halfWidth * 2, height, halfDepth * 2);
      const { geometry, material } = this.propAsset(group.def);
      const mesh = new THREE.InstancedMesh(geometry, material, group.propIndices.length);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      // The hull the simulation actually collides with, drawn over the model
      // that stands in for it. Hidden unless F4 asks — see `setHullsVisible`.
      const hulls = new THREE.InstancedMesh(hullBox, hullMaterial, group.propIndices.length);
      hulls.visible = false;
      this.hullMeshes.push(hulls);

      group.propIndices.forEach((propIndex, slot) => {
        const placed = map.props[propIndex]!;
        composeInstanceMatrix(matrix, 0, scale, placed.x, placed.y + height / 2, placed.z);
        mesh.setMatrixAt(slot, matrix);
        hulls.setMatrixAt(slot, matrix);
        this.propInstances.set(propIndex, { mesh, hulls, slot, scale, halfHeight: height / 2 });
      });
      mesh.instanceMatrix.needsUpdate = true;
      hulls.instanceMatrix.needsUpdate = true;

      this.scene.add(mesh);
      this.scene.add(hulls);
    }
  }

  /**
   * The shared geometry and material for a prop kind, built on first use.
   *
   * Keyed by `PropDef.mesh` rather than by id, because that is the key the
   * catalogue has always carried for exactly this ("Client-side asset key.
   * Never loaded server-side") and because two catalogue entries that name the
   * same mesh should be the same object, not two copies of it.
   */
  private propAsset(def: PropDef): {
    geometry: THREE.BufferGeometry;
    material: THREE.MeshStandardMaterial;
  } {
    let asset = this.propAssets.get(def.mesh);
    if (asset === undefined) {
      asset = { geometry: propGeometry(def.mesh), material: propMaterial(def, this.anisotropy) };
      this.propAssets.set(def.mesh, asset);
    }
    return asset;
  }

  /**
   * Show or hide the collision hull over every prop.
   *
   * A prop mesh may be smaller than its hull — a barrel is a cylinder inscribed
   * in a box, so shooting its corner connects with nothing visible. That is a
   * deliberate trade for props that look like props, and this is the thing that
   * keeps it honest: the discrepancy is one keypress away rather than something
   * you discover by missing.
   */
  setHullsVisible(visible: boolean): void {
    for (const mesh of this.hullMeshes) mesh.visible = visible;
  }

  /**
   * Move one prop instance to its latest interpolated pose.
   *
   * `propIndex` is what a `Msg.Snapshot` entity id decodes to via
   * `propIndexFromEntityId` — the caller's job, not this class's, since this
   * file knows nothing about the wire protocol. A `propIndex` this level never
   * placed (or placed with an uncatalogued id) has no entry in
   * `propInstances` and is silently ignored, the same "unknown id, do
   * nothing" contract `setAvatarPose` already has for an id with no avatar.
   */
  setPropPose(propIndex: number, pose: { x: number; y: number; z: number; yaw: number }): void {
    const entry = this.propInstances.get(propIndex);
    if (entry === undefined) return;
    composeInstanceMatrix(
      this.propMatrixScratch,
      pose.yaw,
      entry.scale,
      pose.x,
      pose.y + entry.halfHeight,
      pose.z,
    );
    entry.mesh.setMatrixAt(entry.slot, this.propMatrixScratch);
    entry.mesh.instanceMatrix.needsUpdate = true;
    // The hull moves with the model or it stops being evidence about it.
    entry.hulls.setMatrixAt(entry.slot, this.propMatrixScratch);
    entry.hulls.instanceMatrix.needsUpdate = true;
  }

  /** Create/remove avatar objects so they match the set of live entity ids. */
  syncAvatars(ids: Iterable<number>): void {
    const wanted = new Set(ids);
    for (const [id, group] of this.avatars) {
      if (wanted.has(id)) continue;
      this.scene.remove(group);
      this.avatars.delete(id);
      this.avatarMaterials.delete(id);
      this.avatarProps.delete(id);
    }
    for (const id of wanted) {
      if (this.avatars.has(id)) continue;
      this.scene.add(this.createAvatar(id));
    }
  }

  private createAvatar(id: number): THREE.Group {
    const material = new THREE.MeshStandardMaterial({ color: colorForId(id), roughness: 0.6 });
    const group = new THREE.Group();

    const body = new THREE.Mesh(this.bodyGeometry, material);
    // Hull y is the feet; the box is centred, so lift it by half its height.
    body.position.y = DEFAULT_HULL.height / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // A snout, so facing is readable at a glance. Without it a cube gives no
    // clue whether remote yaw interpolation is working at all.
    const nose = new THREE.Mesh(this.noseGeometry, material);
    nose.rotation.x = -Math.PI / 2;
    nose.position.set(0, PLAYER_EYE_HEIGHT, -DEFAULT_HULL.halfDepth - 0.15);
    nose.castShadow = true;
    group.add(nose);

    this.avatars.set(id, group);
    this.avatarMaterials.set(id, material);
    this.avatarProps.set(id, 0);
    return group;
  }

  /**
   * Reshape an avatar's body to match what it is disguised as.
   *
   * Dimensions come from `PROP_CATALOGUE`, which is the same object the
   * simulation takes hulls from — there is no second table of prop sizes here
   * that could drift from the collision one.
   *
   * A disguised avatar is given the *same* geometry and the *same* material
   * object the kind's `InstancedMesh` uses (`propAsset`), not a copy sized to
   * match. That is the whole point: a crate a player is wearing has to be
   * indistinguishable from the crates it is standing among, and "two code paths
   * that build equal-looking things" is a weaker guarantee than "one thing".
   * Since prop geometry is authored inside the unit cube, wearing it is a
   * scale — the same `halfWidth*2, height, halfDepth*2` the instanced props are
   * posed with.
   *
   * The nose is hidden while disguised. It exists to make a player's facing
   * readable, and a crate that advertises which way it is looking is a crate
   * that is obviously a player. Yaw still rotates the mesh, which is correct:
   * prop footprints are square by validator rule, so turning changes what you
   * see without changing what you hit.
   */
  private reshapeAvatar(id: number, group: THREE.Group, propId: number): void {
    const body = group.children[0];
    const nose = group.children[1];
    if (!(body instanceof THREE.Mesh)) return;

    const def = this.catalogue.get(propId);
    const own = this.avatarMaterials.get(id);

    if (def === undefined) {
      body.geometry = this.bodyGeometry;
      body.scale.set(1, 1, 1);
      body.position.y = DEFAULT_HULL.height / 2;
      if (own !== undefined) body.material = own;
    } else {
      const { geometry, material } = this.propAsset(def);
      const { halfWidth, halfDepth, height } = def.hull;
      body.geometry = geometry;
      body.scale.set(halfWidth * 2, height, halfDepth * 2);
      // Hull y is the feet; the mesh is centred, so lift it by half its height.
      body.position.y = height / 2;
      body.material = material;
    }

    if (nose !== undefined) nose.visible = def === undefined;
  }

  setAvatarPose(id: number, pose: AvatarPose): void {
    const group = this.avatars.get(id);
    if (group === undefined) return;
    group.position.set(pose.x, pose.y, pose.z);
    group.rotation.y = pose.yaw;

    if (this.avatarProps.get(id) !== pose.propId) {
      this.avatarProps.set(id, pose.propId);
      this.reshapeAvatar(id, group, pose.propId);
    }

    const material = this.avatarMaterials.get(id);
    if (material !== undefined) {
      // Red when we have run off the end of the interpolation buffer and are
      // holding a stale pose. Makes a network problem look like a network
      // problem instead of like a physics bug.
      //
      // Only ever applied to an avatar's *own* material, never to the shared
      // prop one — tinting that would turn every crate in the level red, and
      // would announce which crate is a struggling player. The disguised case
      // is silent for that reason as much as for the first.
      material.emissive.setHex(pose.starved ? 0x662222 : 0x000000);
    }
  }

  /**
   * `crouching` lowers the eye to CROUCH_EYE_HEIGHT.
   *
   * That constant was defined in M0 and never used, so crouching moved the
   * hull without moving the camera. Cosmetic until M4 — a shot now leaves from
   * `shotOrigin`, which uses the same `eyeHeightFor`, so a camera that
   * disagreed would put a crouched hunter's bullets somewhere other than their
   * crosshair.
   */
  render(
    camX: number, camY: number, camZ: number,
    yaw: number, pitch: number,
    crouching: boolean,
  ): void {
    this.camera.position.set(camX, camY + eyeHeightFor(crouching), camZ);
    // Applied live at render rate rather than at the 20Hz simulation rate:
    // position can afford to be a tick behind, aiming cannot.
    this.camera.rotation.y = yaw;
    this.camera.rotation.x = pitch;
    // Reset here rather than letting the renderer do it: its own autoReset runs
    // *after* the shadow pass has already been counted and discarded, so the F3
    // overlay would report the main pass and call it the frame. `autoReset` is
    // off in the constructor for this.
    this.renderer.info.reset();
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Draw calls issued by the last `render`, straight from the WebGL renderer.
   *
   * Includes the shadow pass, which roughly doubles the count — every mesh that
   * casts is drawn again into the depth map. That is the honest number and the
   * one worth watching, since it is what the GPU was asked to do.
   */
  get drawCalls(): number {
    return this.renderer.info.render.calls;
  }

  /** Triangles rasterised by the last `render`. */
  get triangles(): number {
    return this.renderer.info.render.triangles;
  }

  resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    // Set here rather than once in the constructor: dragging the window to a
    // monitor with a different DPI changes devicePixelRatio, and a stale value
    // means rendering at the wrong resolution for the display. Capped at 2
    // because the cost is quadratic in it and the returns past 2x are not
    // visible on a fast-moving first-person view.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
  }

  /**
   * Release all GPU and CPU resources held by this renderer.
   *
   * Call this when the game ends or the player disconnects. Failure to dispose
   * leaks textures, materials, and geometries in both GPU memory and CPU side
   * arrays.
   */
  dispose(): void {
    for (const material of this.createdMaterials) material.dispose();
    for (const texture of this.createdTextures) texture.dispose();
    for (const geometry of this.createdGeometries) geometry.dispose();

    this.bodyGeometry.dispose();
    this.noseGeometry.dispose();

    for (const asset of this.propAssets.values()) {
      asset.geometry.dispose();
      asset.material.dispose();
    }

    this.renderer.dispose();
  }
}

/**
 * Write `T(pos) * R(yaw) * S(scale)` into `out`, as a single instance
 * transform for the shared unit-cube geometry.
 *
 * `makeRotationY` then `.scale` composes rotation and scale in the 3x3 block
 * (scale applied in the cube's local space, then rotated — the order that
 * keeps a non-uniform box's faces aligned with its rotated axes rather than
 * the world's); `setPosition` then overwrites only the translation column,
 * leaving that block intact. Shared by the static wall/prop-instance seed and
 * every live `setPropPose` update so the two can never compose a transform
 * differently.
 */
function composeInstanceMatrix(
  out: THREE.Matrix4,
  yaw: number,
  scale: THREE.Vector3,
  x: number,
  y: number,
  z: number,
): void {
  out.makeRotationY(yaw);
  out.scale(scale);
  out.setPosition(x, y, z);
}
